import React from "react";

// Caesar — modern AI-assistant avatar
// A soft, iridescent orb-style avatar inspired by contemporary AI product design:
// - Subtle multi-stop mesh gradient (indigo → violet → teal)
// - Diagonal light sweep for a "living" feel
// - Minimal geometric "Caesar spark" glyph in the center
// - No text/serif letterforms — pure glyph, works at every size
export const CaesarAvatar = ({ size = 40, className = "" }) => {
    const id = React.useId();
    return (
        <svg
            viewBox="0 0 64 64"
            width={size}
            height={size}
            className={className}
            aria-hidden="true"
        >
            <defs>
                {/* Base mesh gradient — soft mint + navy for FlowMint palette */}
                <radialGradient id={`bg-${id}`} cx="30%" cy="30%" r="90%">
                    <stop offset="0%" stopColor="#A6E4C5" />
                    <stop offset="45%" stopColor="#3AA37F" />
                    <stop offset="100%" stopColor="#0D1E2E" />
                </radialGradient>
                {/* Diagonal sheen */}
                <linearGradient id={`sheen-${id}`} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.35" />
                    <stop offset="40%" stopColor="#FFFFFF" stopOpacity="0" />
                    <stop offset="100%" stopColor="#7DD3B0" stopOpacity="0.35" />
                </linearGradient>
                {/* Halo ring */}
                <linearGradient id={`ring-${id}`} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#A6E4C5" />
                    <stop offset="55%" stopColor="#7DD3B0" />
                    <stop offset="100%" stopColor="#F9AE7C" />
                </linearGradient>
                {/* Inner glow filter */}
                <filter id={`glow-${id}`} x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="0.8" />
                </filter>
            </defs>

            {/* Halo ring */}
            <circle cx="32" cy="32" r="30" fill="none" stroke={`url(#ring-${id})`} strokeWidth="1.6" opacity="0.85" />

            {/* Orb body */}
            <circle cx="32" cy="32" r="28" fill={`url(#bg-${id})`} />

            {/* Diagonal sheen overlay */}
            <circle cx="32" cy="32" r="28" fill={`url(#sheen-${id})`} />

            {/* Top-left highlight */}
            <ellipse cx="22" cy="18" rx="12" ry="6" fill="#FFFFFF" opacity="0.22" />

            {/* Minimal "spark" glyph — 4-point star with subtle rotation */}
            <g transform="translate(32 33) rotate(12)" filter={`url(#glow-${id})`}>
                {/* vertical beam */}
                <path
                    d="M 0 -13 Q 2 -3 0 0 Q -2 -3 0 -13 Z M 0 13 Q 2 3 0 0 Q -2 3 0 13 Z"
                    fill="#FFFFFF"
                    opacity="0.95"
                />
                {/* horizontal beam */}
                <path
                    d="M -13 0 Q -3 -2 0 0 Q -3 2 -13 0 Z M 13 0 Q 3 -2 0 0 Q 3 2 13 0 Z"
                    fill="#FFFFFF"
                    opacity="0.75"
                />
                {/* center pearl */}
                <circle cx="0" cy="0" r="2.4" fill="#FFFFFF" />
                <circle cx="-0.7" cy="-0.7" r="0.9" fill="#DFF0E5" opacity="0.9" />
            </g>

            {/* Tiny "presence" pulse — a subtle floating dot lower-right for movement */}
            <circle cx="49" cy="46" r="1.6" fill="#7DD3B0" opacity="0.95" />
        </svg>
    );
};

export default CaesarAvatar;
